import AppleStoreIcon from '@/components/icons/AppleStoreIcon/AppleStoreIcon';
import FacebookIcon from '@/components/icons/FacebookIcon/FacebookIcon';
import GooglePlayIcon from '@/components/icons/GooglePlayIcon/GooglePlayIcon';
import LinkedInIcon from '@/components/icons/LinkedInIcon/LinkedInIcon';
import { RVULogoNew } from '@/components/icons/RVULogo/RVULogoNew';
import { TempcoverLogo } from '@/components/icons/TempcoverLogo/TempcoverLogo';
import YoutubeIcon from '@/components/icons/YoutubeIcon/YoutubeIcon';
import { FooterData, SocialLink } from '@/types/footer';
import { IconBrandInstagramFilled, IconBrandTiktokFilled, IconBrandXFilled } from '@tabler/icons-react';
import { type ComponentType, type SVGProps } from 'react';

type SocialIconProps = { size?: number; className?: string };

function wrapSvgIcon(SvgComponent: ComponentType<SVGProps<SVGSVGElement>>) {
  const Wrapped = ({ size, className }: SocialIconProps) => (
    <SvgComponent width={size} height={size} className={className} fill="currentColor" />
  );
  Wrapped.displayName = SvgComponent.displayName || SvgComponent.name;
  return Wrapped;
}

const SOCIAL_ICON_MAP: Record<string, ComponentType<SocialIconProps>> = {
  facebook: wrapSvgIcon(FacebookIcon),
  instagram: IconBrandInstagramFilled,
  tiktok: IconBrandTiktokFilled,
  youtube: wrapSvgIcon(YoutubeIcon),
  linkedin: wrapSvgIcon(LinkedInIcon),
  x: IconBrandXFilled,
};

const DEFAULT_SOCIAL_LINKS: SocialLink[] = [
  {
    title: 'Facebook',
    url: 'https://www.facebook.com/tempcover?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'facebook',
  },
  {
    title: 'Instagram',
    url: 'https://www.instagram.com/tempcoverofficial/?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'instagram',
  },
  {
    title: 'TikTok',
    url: 'https://www.tiktok.com/@tempcover?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'tiktok',
  },
  {
    title: 'YouTube',
    url: 'https://www.youtube.com/@tempcover?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'youtube',
  },
  {
    title: 'LinkedIn',
    url: 'https://www.linkedin.com/company/2320990/?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'linkedin',
  },
  {
    title: 'X',
    url: 'https://x.com/tempcover?utm_source=social&utm_medium=website&utm_campaign=footer&utm_id=smlink',
    iconName: 'x',
  },
];

function Footer({ data }: { data: FooterData }) {
  if (!data) return null;

  return (
    <footer>
      <div className="tc-container py-16">
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_1fr_1fr]">
          {/* Copyright and address */}
          <div className="order-1 flex flex-col items-center justify-between md:items-start">
            <TempcoverLogo className="mb-4 lg:mb-6" />

            {/* Address and Copyright - Desktop */}
            <div className="hidden flex-col gap-2 lg:flex">
              <div dangerouslySetInnerHTML={{ __html: data.info }} />
            </div>
          </div>

          {/* Links */}
          <div className="order-4 flex justify-between md:order-2">
            <nav aria-label={data.section1Title}>
              <h2 className="font-base text-[18px] lg:text-[23px]">{data.section1Title}</h2>
              <ul>
                {data.section1Items.map((item) => (
                  <li key={item.url}>
                    <a
                      href={item.url}
                      target={item.isExternal ? '_blank' : '_self'}
                      {...(item.isExternal ? { rel: 'noopener noreferrer' } : {})}
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-label={data.section2Title} className="text-right lg:text-left">
              <h2 className="font-base text-[18px] lg:text-[23px]">{data.section2Title}</h2>
              <ul>
                {data.section2Items.map((item) => (
                  <li key={item.url}>
                    <a
                      href={item.url}
                      target={item.isExternal ? '_blank' : '_self'}
                      {...(item.isExternal ? { rel: 'noopener noreferrer' } : {})}
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Social Media Links & Signup */}
          <div className="order-2 lg:order-3">
            <h2 className="font-base text-[18px] lg:text-[23px]">{data.section3Title}</h2>
            <div dangerouslySetInnerHTML={{ __html: data.section3Content }} />

            <ul className="mt-4 flex flex-row gap-[10px] lg:mt-6">
              {(data.socialLinks?.length ? data.socialLinks : DEFAULT_SOCIAL_LINKS).map(({ title, url, iconName }) => {
                const Icon = SOCIAL_ICON_MAP[iconName.toLowerCase()];
                if (!Icon) return null;
                return (
                  <li key={title}>
                    <a aria-label={title} href={url} target="_blank" rel="noopener noreferrer">
                      <div className="bg-primary active:bg-primary-400 flex h-[32px] w-[32px] items-center justify-center rounded">
                        <Icon size={20} className="text-white" />
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* App Download Links */}
          <div className="order-3 py-2 md:order-4 md:col-start-1">
            <h2 className="font-base text-[18px] md:hidden lg:font-[23px]">Get the app</h2>
            <div className="flex gap-[14px]">
              <AppStoreLinks appStore={data.storeLinks.appStoreLink} playStore={data.storeLinks.playStoreLink} />
            </div>
          </div>
        </div>

        <div
          className="mt-4 mb-4 w-full text-center md:hidden md:text-left [&_p]:text-[12px]"
          dangerouslySetInnerHTML={{ __html: data.secondaryContent ?? '' }}
        />
        <div
          className="mt-4 hidden w-full text-center md:block md:text-left [&_p]:text-[14px]"
          dangerouslySetInnerHTML={{ __html: data.secondaryContent ?? '' }}
        />

        {/* Address and Copyright - Mobile */}
        <div className="order-5 block text-center md:hidden [&_p]:text-[12px]">
          <div dangerouslySetInnerHTML={{ __html: data.info }} />
        </div>

        {/* Logo and Copyright */}
        <section className="border-dark-200 border-t bg-gray-50 md:mt-8">
          <div className="mx-auto flex max-w-7xl flex-col-reverse items-center justify-between md:flex-row md:py-6">
            <a
              href="https://www.rvu.co.uk/"
              target="_blank"
              rel="noreferrer"
              aria-label="Open RVU website in a new tab"
            >
              <RVULogoNew width={75} className="w-[50px] md:w-[75px]" />
            </a>
            <p className="mt-4 text-[14px] text-gray-700 md:text-[16px]">{data.footNote}</p>
          </div>
        </section>
      </div>
    </footer>
  );
}

export const AppStoreLinks = ({ appStore, playStore }: { appStore: string; playStore: string }) => (
  <>
    <a aria-label="iOS App Store" href={appStore} target="_blank" rel="noopener noreferrer" className="h-[48px]">
      <AppleStoreIcon />
    </a>
    <a
      aria-label="Google Play Store"
      href={playStore}
      target="_blank"
      rel="noopener noreferrer"
      className="h-[48px] w-[120px]"
    >
      <GooglePlayIcon className="w-[135px]" />
    </a>
  </>
);

export default Footer;
